/**
 * One-click add to cart for the cart-drawer cross-sell cards.
 *
 * The bag button on a cross-sell card used to open the quick-buy drawer
 * (assets/quick-add.js). It now posts the variant straight to /cart/add.js and
 * re-renders the drawer in place, so the customer never leaves the cart.
 * The card body still opens quick-buy — see snippets/shoppable-product-card.liquid.
 *
 * The variant is whatever Liquid resolved as
 * `product.selected_or_first_available_variant`, so a multi-variant product
 * adds its first available option rather than asking the customer to choose.
 *
 * Refresh follows the same path as assets/cart-service-checkbox.js: call
 * `refreshCartDrawer()` on the <cart-drawer> element, then publish `cartUpdate`
 * with this component's source. The source is registered in
 * CART_ITEMS_SELF_REFRESH_SOURCES (assets/cart.js) so <cart-drawer-items>
 * does not fire a second, redundant fetch of its own.
 *
 * `refreshCartDrawer()` replaces the innerHTML of #CartDrawer, which contains
 * this element — anything after the refresh must not touch `this`.
 *
 * PDP video reels reuse the component with two opt-in attributes
 * (snippets/shoppable-product-card.liquid, `direct_add`):
 * - `data-close-reels`: close the <modal-swiper> the card lives in,
 * - `data-open-drawer`: open the cart drawer after the refresh, so the
 *   customer sees the product land in the cart without a quick-buy step.
 */
class CrossSellAdd extends HTMLElement {
  static SOURCE = "cross-sell-add";

  connectedCallback() {
    this.button = this.querySelector("button");
    if (!this.button) return;

    this.variantId = parseInt(this.dataset.variantId, 10);
    if (!this.variantId) return;

    this.handleClick = this.handleClick.bind(this);
    this.button.addEventListener("click", this.handleClick);
  }

  disconnectedCallback() {
    if (this.button && this.handleClick) {
      this.button.removeEventListener("click", this.handleClick);
    }
  }

  async handleClick(event) {
    event.preventDefault();
    if (this.loading) return;

    this.setLoading(true);

    // Read the flags before the refresh: it can replace this element.
    const closeReels = this.hasAttribute("data-close-reels");
    const openDrawer = this.hasAttribute("data-open-drawer");

    try {
      await this.addToCart();
      await this.refreshCart();
      if (closeReels) this.closeReelsModal();
      if (openDrawer) this.openCartDrawer();
    } catch (error) {
      console.error("[cross-sell-add]", error);
      this.setLoading(false);
    }
  }

  async addToCart() {
    const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        items: [{ id: this.variantId, quantity: 1 }],
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.description || "Add to cart failed");
    }

    return response.json();
  }

  async refreshCart() {
    const cartDrawer = document.querySelector("cart-drawer");
    if (cartDrawer && typeof cartDrawer.refreshCartDrawer === "function") {
      // Re-renders #CartDrawer without toggling it, so the drawer stays open
      // and the just-added product drops out of the cross-sell list.
      await cartDrawer.refreshCartDrawer();
    } else {
      // The cart page has no drawer to refresh, so re-render its own sections.
      await this.refreshCartPage();
    }

    if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: CrossSellAdd.SOURCE });
    }
  }

  /**
   * Re-render the cart page in place. Same approach as
   * assets/cart-service-checkbox.js, so both surfaces stay consistent.
   */
  async refreshCartPage() {
    const cartItemsEl = document.getElementById("main-cart-items");
    const cartFooterEl = document.getElementById("main-cart-footer");
    if (!cartItemsEl && !cartFooterEl) return;

    const sectionIds = [];
    if (cartItemsEl?.dataset.id) sectionIds.push(cartItemsEl.dataset.id);
    if (cartFooterEl?.dataset.id) sectionIds.push(cartFooterEl.dataset.id);
    sectionIds.push("cart-icon-bubble");

    const response = await fetch(
      `${window.Shopify.routes.root}?sections=${sectionIds.join(",")}`,
    );
    if (!response.ok) throw new Error(`sections responded ${response.status}`);
    const data = await response.json();

    const targets = [
      { key: cartItemsEl?.dataset.id, el: cartItemsEl, selector: ".js-contents" },
      { key: cartFooterEl?.dataset.id, el: cartFooterEl, selector: ".js-contents" },
      {
        key: "cart-icon-bubble",
        // This theme renders the section straight into the header link, there
        // is no wrapping .shopify-section element to replace inside it.
        el: document.getElementById("cart-icon-bubble"),
        selector: ".shopify-section",
        replaceSelf: true,
      },
    ];

    targets.forEach((target) => {
      if (!target.key || !target.el || !data[target.key]) return;
      const parsed = new DOMParser().parseFromString(
        data[target.key],
        "text/html",
      );
      const incoming = parsed.querySelector(target.selector);
      if (!incoming) return;
      if (target.replaceSelf) {
        target.el.innerHTML = incoming.innerHTML;
      } else {
        const existing = target.el.querySelector(target.selector);
        if (existing) existing.innerHTML = incoming.innerHTML;
      }
      // A quantity being edited must not repaint with the cart's older value.
      window.cartQuantityIntent?.restore();
    });
  }

  closeReelsModal() {
    // <modal-swiper> is never attached to the DOM, only its overlay is, so the
    // overlay's close button (wired to modal.close()) is the reliable handle.
    const overlay = this.closest(".video-reels-modal");
    overlay?.querySelector(".video-reels-modal__close")?.click();
  }

  openCartDrawer() {
    const cartDrawer = document.querySelector("cart-drawer");
    if (!cartDrawer || cartDrawer.isOpen) return;
    // toggleDrawerClasses() is the full open path (open attribute + drawer and
    // body classes + cartDrawerOpen event), the same one product-form.js and
    // the header bag icon use. onToggle() alone only sets the attribute and
    // leaves the close button toggling the classes the wrong way.
    if (typeof cartDrawer.toggleDrawerClasses === "function") {
      cartDrawer.toggleDrawerClasses();
    } else {
      document.querySelector(".wt-cart__trigger")?.click();
    }
  }

  setLoading(loading) {
    this.loading = loading;
    this.toggleAttribute("loading", loading);

    // The overlay replaces the button outright, the same way a cart line's
    // quantity update does — CSS hides the siblings that follow it.
    const overlay = this.querySelector(".loading-overlay");
    if (overlay) overlay.classList.toggle("hidden", !loading);

    if (this.button) this.button.disabled = loading;
  }
}

if (!customElements.get("cross-sell-add")) {
  customElements.define("cross-sell-add", CrossSellAdd);
}
